// 出展者ステータス / admin_actions の action 名を日本語ラベルにマップする
// 単一のソース。public-site と admin-dashboard-2026 の両方で使う。
//
// public-site 側 (利用者向け文言) と admin 側 (運営向け文言) を別系統にしておく。
// 例: paid を利用者には「支払い完了」、運営には「支払済」と短く出す、など語感の差を吸収する。

// ─── 出展者ステータス ────────────────────────────────────────────

import type { ExhibitorStatus, ExhibitorType } from './exhibitor/core';

export const EXHIBITOR_STATUS_ORDER: ExhibitorStatus[] = [
  'entered',
  'checkedByAdmin',
  'waitingForPay',
  'paid',
  'waitingList',
  'cancelled',
];

// admin 側で使う短いラベル (運営向け)。
export const ADMIN_STATUS_LABEL: Record<ExhibitorStatus, string> = {
  entered: '申込完了',
  checkedByAdmin: '内容確認済',
  waitingList: 'キャンセル待ち',
  waitingForPay: '支払い待ち',
  paid: '支払済',
  cancelled: 'キャンセル',
};

// admin のステータス凡例 Tooltip に出す説明文。
// 「次のアクションが何で、今のステップで何ができるか」を簡潔に。
export const ADMIN_STATUS_DESCRIPTION: Record<ExhibitorStatus, string> = {
  entered:
    '申込フォーム送信直後の状態。運営による内容チェック前。出展者は内容を編集できる。',
  checkedByAdmin:
    '紙運用で内容確認が完了した状態。料金調整・内定通知メール送信に進める。',
  waitingForPay:
    '内定通知メール + 請求メール送信済の状態。出展者がクレジットカード決済を行う段階。',
  paid: '支払い完了。出展確定。公開リストにも掲載される。',
  waitingList: '上限に達したため一時保留。空き次第キャンセル待ちから繰り上げる。',
  cancelled: 'キャンセル済。公開リストからは外れる。',
};

// admin の Badge variant マッピング。終端 (paid) と中間 (checkedByAdmin など) で色を分け、
// 「支払済」と「内容確認済」を視覚的に取り違えないようにする。
export const ADMIN_STATUS_VARIANT: Record<
  ExhibitorStatus,
  'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'outline'
> = {
  entered: 'outline',
  checkedByAdmin: 'secondary',
  waitingList: 'warning',
  waitingForPay: 'warning',
  paid: 'success',
  cancelled: 'secondary',
};

// 公開サイト側ラベル (利用者向け、文脈を補う長めの文言)。
export const PUBLIC_STATUS_LABEL: Record<ExhibitorStatus, string> = {
  entered: '申込完了 (運営確認待ち)',
  checkedByAdmin: '運営確認済 (料金確定待ち)',
  waitingList: 'キャンセル待ち',
  waitingForPay: '支払い待ち',
  paid: '支払い完了',
  cancelled: 'キャンセル',
};

// 公開サイトでのバッジ色 (Mantine 系 color name)。
export const PUBLIC_STATUS_COLOR: Record<ExhibitorStatus, string> = {
  entered: 'gray',
  checkedByAdmin: 'yellow',
  waitingList: 'gray',
  waitingForPay: 'orange',
  paid: 'green',
  cancelled: 'gray',
};

export function adminStatusLabel(status: string): string {
  return ADMIN_STATUS_LABEL[status as ExhibitorStatus] ?? status;
}

export function publicStatusLabel(status: string): string {
  return PUBLIC_STATUS_LABEL[status as ExhibitorStatus] ?? status;
}

// ─── 出展タイプ ────────────────────────────────────────────────

// 請求メール・マイページの支払いセクションなど「本人はもう自分の種別を知っている」
// 文脈向けの短いラベル。申込フォームの説明的なラベル (「一般 (学割なし)」等、料金の
// 判断基準を説明する文言) とは目的が違うので統合しない。
export const EXHIBITOR_TYPE_LABEL: Record<ExhibitorType, string> = {
  general: '一般',
  'student-university': '学生 (大学)',
  'student-high-school': '学生 (高校以下)',
  corporate: '企業',
};

export function exhibitorTypeLabel(type: string): string {
  return EXHIBITOR_TYPE_LABEL[type as ExhibitorType] ?? type;
}

// ─── admin_actions の action 名 ──────────────────────────────────

export type AdminActionType =
  | 'status_change'
  | 'send_approval_email'
  | 'send_payment_notification'
  | 'send_payment_reminder'
  | 'manual_email_send'
  | 'fees_update'
  | 'stripe_sync'
  | 'exhibitor_update'
  | 'exhibitor_partial_update'
  | 'exhibitor_delete';

export const ADMIN_ACTION_LABEL: Record<string, string> = {
  status_change: 'ステータス変更',
  send_approval_email: '内定通知メール送信',
  send_payment_notification: '請求メール送信',
  send_payment_reminder: '支払いリマインド送信',
  manual_email_send: '個別メール送信',
  fees_update: '料金更新',
  stripe_sync: 'Stripe 価格同期',
  exhibitor_update: '出展者情報更新',
  exhibitor_partial_update: '出展者情報の一部更新',
  exhibitor_delete: '出展者削除',
};

export function adminActionLabel(action: string): string {
  return ADMIN_ACTION_LABEL[action] ?? action;
}

// admin_actions.details の中に書かれる「状態遷移」文字列を日本語化する。
// 例: 'entered → checkedByAdmin: 内容確認' → '申込完了 → 内容確認済: 内容確認'
//
// admin.ts:608 で `${exhibitor.status} → ${nextStatus}${reason ? `: ${reason}` : ''}` の形で書き込んでいる。
// UI 表示時にこの関数を通す。
export function humanizeStatusTransition(details: string): string {
  // ASCII の `->` と全角の `→` の両方を許容する。
  return details.replace(
    /\b(entered|checkedByAdmin|waitingList|waitingForPay|paid|cancelled)\s*(→|->)\s*(entered|checkedByAdmin|waitingList|waitingForPay|paid|cancelled)\b/g,
    (_match, from: ExhibitorStatus, _arrow: string, to: ExhibitorStatus) =>
      `${ADMIN_STATUS_LABEL[from] ?? from} → ${ADMIN_STATUS_LABEL[to] ?? to}`
  );
}
